import { useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

// One idempotency key per logical money-moving attempt (withdraw, bill payment).
//
// - Retrying after a request got NO response (timeout / network drop) reuses the
//   same key, so if the first request did land, the backend dedupes instead of
//   debiting twice.
// - Once the server has answered (success, wrong PIN, insufficient funds…) the
//   key is spent — the backend replays the stored response for a reused key —
//   so we rotate to a fresh one.
//
// The key lives as long as the component, so going back to edit the form (which
// unmounts the preview) always starts a new attempt.
// Mirrors deexoptions-mobile-app/utils/baseAxios.ts (mutationRetry notes).
const useIdempotencyKey = () => {
  const keyRef = useRef(uuidv4());

  const rotate = useCallback(() => {
    keyRef.current = uuidv4();
  }, []);

  // Pass as axios config: axiosInstance.post(url, body, idempotency.config())
  const config = useCallback(
    () => ({ headers: { "idempotency-key": keyRef.current } }),
    []
  );

  // Errors from useAuthAxios are normalized: `status` is null only when the
  // server never responded.
  const rotateIfAnswered = useCallback(
    (error: unknown) => {
      if ((error as { status?: number | null } | null)?.status != null) {
        rotate();
      }
    },
    [rotate]
  );

  return { config, rotate, rotateIfAnswered };
};

export default useIdempotencyKey;
